// Email-safe signature builder for /tools/email-signature-generator.
// Pure functions: table layout with inline styles (what Gmail, Outlook and
// Apple Mail render reliably). Every user value is escaped; links must be
// http(s), so pasted text can never inject script or javascript: URLs.

export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Accepts "riverastudio.com" as well as full URLs; returns '' for anything
// that is not a plain http(s) URL.
export function safeUrl(value) {
  const raw = String(value ?? '').trim();
  if (!raw || /\s/.test(raw)) return '';
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    if (!url.hostname.includes('.')) return '';
    return url.href;
  } catch {
    return '';
  }
}

export const safeColor = (value) => (/^#[0-9a-f]{6}$/i.test(String(value ?? '')) ? value : '#0e7490');

export const safeEmail = (value) => {
  const raw = String(value ?? '').trim();
  return /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(raw) ? raw : '';
};

export const telHref = (value) => {
  const digits = String(value ?? '').replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
  return digits.replace(/\D/g, '').length >= 5 ? `tel:${digits}` : '';
};

const displayUrl = (href) => href.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

const FONT = 'font-family:Arial,Helvetica,sans-serif;';

function link(href, text, color, extra = '') {
  return `<a href="${escapeHtml(href)}" style="color:${color};text-decoration:none;${extra}">${escapeHtml(text)}</a>`;
}

function contactLines(d, color) {
  const lines = [];
  const email = safeEmail(d.email);
  const tel = telHref(d.phone);
  const website = safeUrl(d.website);
  if (email) lines.push(link(`mailto:${email}`, email, color));
  if (tel) lines.push(link(tel, d.phone.trim(), color));
  if (website) lines.push(link(website, displayUrl(website), color));
  return lines;
}

function socialLine(d, color) {
  const items = [];
  const linkedin = safeUrl(d.linkedin);
  const instagram = safeUrl(d.instagram);
  if (linkedin) items.push(link(linkedin, 'LinkedIn', color, 'font-weight:bold;'));
  if (instagram) items.push(link(instagram, 'Instagram', color, 'font-weight:bold;'));
  return items.join('&nbsp;&nbsp;·&nbsp;&nbsp;');
}

function bookingButton(d, color) {
  const href = safeUrl(d.booking);
  if (!href) return '';
  const label = String(d.bookingLabel || '').trim() || 'Book a call';
  return `<table cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;border-collapse:collapse;"><tr><td style="background:${color};border-radius:4px;padding:6px 12px;">`
    + `<a href="${escapeHtml(href)}" style="${FONT}font-size:13px;font-weight:bold;color:#ffffff;text-decoration:none;">${escapeHtml(label)}</a>`
    + '</td></tr></table>';
}

function identity(d, color) {
  const name = escapeHtml(String(d.name || '').trim());
  const role = [d.title, d.company].map((v) => String(v || '').trim()).filter(Boolean).map(escapeHtml).join(' · ');
  return (name ? `<div style="${FONT}font-size:16px;font-weight:bold;color:#111827;line-height:1.3;">${name}</div>` : '')
    + (role ? `<div style="${FONT}font-size:13px;color:${color};line-height:1.4;">${role}</div>` : '');
}

export function buildSignatureHtml(details = {}) {
  const color = safeColor(details.color);
  const contacts = contactLines(details, color);
  const social = socialLine(details, color);
  const button = bookingButton(details, color);
  const image = safeUrl(details.image);
  const textStyle = `${FONT}font-size:13px;color:#4b5563;line-height:1.5;`;

  if (details.layout === 'compact') {
    const line = [...contacts, social].filter(Boolean).join('&nbsp;&nbsp;|&nbsp;&nbsp;');
    return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="${textStyle}">`
      + identity(details, color)
      + (line ? `<div style="${textStyle}margin-top:4px;">${line}</div>` : '')
      + button
      + '</td></tr></table>';
  }

  const details_ = `${identity(details, color)}`
    + (contacts.length ? `<div style="${textStyle}margin-top:6px;">${contacts.join('<br>')}</div>` : '')
    + (social ? `<div style="${textStyle}margin-top:6px;">${social}</div>` : '')
    + button;

  if (details.layout === 'photo' && image) {
    return '<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>'
      + `<td style="padding-right:14px;vertical-align:top;"><img src="${escapeHtml(image)}" width="80" height="80" alt="${escapeHtml(String(details.name || details.company || '').trim())}" style="display:block;width:80px;height:80px;border-radius:40px;object-fit:cover;"></td>`
      + `<td style="border-left:2px solid ${color};padding-left:14px;vertical-align:top;">${details_}</td>`
      + '</tr></table>';
  }

  return '<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>'
    + `<td style="border-left:3px solid ${color};padding-left:12px;vertical-align:top;">${details_}</td>`
    + (image ? `<td style="padding-left:14px;vertical-align:top;"><img src="${escapeHtml(image)}" width="64" alt="${escapeHtml(String(details.company || details.name || '').trim())}" style="display:block;width:64px;height:auto;"></td>` : '')
    + '</tr></table>';
}

// Plain-text fallback for mail apps that drop HTML on paste.
export function signatureText(details = {}) {
  const lines = [];
  const name = String(details.name || '').trim();
  const role = [details.title, details.company].map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
  if (name) lines.push(name);
  if (role) lines.push(role);
  const email = safeEmail(details.email);
  if (email) lines.push(email);
  if (String(details.phone || '').trim() && telHref(details.phone)) lines.push(details.phone.trim());
  const website = safeUrl(details.website);
  if (website) lines.push(displayUrl(website));
  const booking = safeUrl(details.booking);
  if (booking) lines.push(`${String(details.bookingLabel || '').trim() || 'Book a call'}: ${booking}`);
  for (const key of ['linkedin', 'instagram']) {
    const url = safeUrl(details[key]);
    if (url) lines.push(url);
  }
  return lines.join('\n');
}
